import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  SimpleGrid,
  Stack,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { useAuth } from 'contexts/AuthContext';
import { useNotification } from 'contexts/NotificationContext';
import CInput from 'components/CInput';
import CButton from 'components/CButton';
import DatePicker from "react-datepicker"
import DateTimePicker from 'components/DateTimePicker';

export default function NewNotification() {
  const { getSingleNotification, createNotification, updateNotification, } = useNotification();
  const { toast } = useAuth();

  const navigate = useNavigate();
  const params = useParams();

  const textColor = useColorModeValue('secondaryGray.900', 'white');

  const [validated, setValidated] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [notificationDetail, setNotificationDetail] = useState({
    title: "",
    description: "",
    scheduled_at: "",
  });

  useEffect(() => {
    fetchSingleNotification();

    return () => {
      setNotificationDetail({
        title: "",
        description: "",
        scheduled_at: "",
      })
    }
  }, [params.id]);

  const fetchSingleNotification = async () => {
    if (params.id) {
      const notification = await getSingleNotification(params.id);
      if (notification) {
        setNotificationDetail(notification);
        if (notification.scheduled_at) {
          setShowSchedule(true)
        }
      }
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidated(true);

    if (!notificationDetail?.title?.trim() || !notificationDetail?.description?.trim()) return;

    try {
      if (params.id) {
        const data = await updateNotification(params.id, notificationDetail);
        if (!data.error) {
          navigate('/admin/notifications');
        }
      } else {
        const data = await createNotification(notificationDetail);
        if (!data.error) {
          navigate('/admin/notifications');
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleDateChange = (date) => {
    if (date instanceof Date && !isNaN(date)) {
      const roundedDate = roundToNearestTenMinutes(date);
      const formattedDate = formatDateToISO(roundedDate);

      setNotificationDetail((prev) => ({
        ...prev,
        scheduled_at: formattedDate,
      }));
    } else {
      setNotificationDetail((prev) => ({
        ...prev,
        scheduled_at: "",
      }));
    }
  };

  const roundToNearestTenMinutes = (date) => {
    const minutes = date.getMinutes();
    const roundedMinutes = Math.round(minutes / 10) * 10;
    date.setMinutes(roundedMinutes);
    return date;
  };

  const formatDateToISO = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const clearDate = () => {
    setNotificationDetail((prev) => ({
      ...prev,
      scheduled_at: "",
    }));
  };

  return (
    <Flex
      direction="column"
      minH="100vh"
      align="start"
      top="0"
      pt={{ sm: '125px', lg: '75px' }}
      position="relative"
      w="100%"
    >
      <Tabs w="100%">
        <TabPanels mt="24px" maxW="100%" w="100%" mx="auto">
          <TabPanel w="100%" p="0px" mx="auto">
            <Text color={textColor} fontSize="2xl" fontWeight="700" mb="20px">
              Notification Info
            </Text>
            <form onSubmit={handleSubmit}>
              <Flex direction="column" w="100%">
                <Stack direction="column" spacing="20px">
                  <SimpleGrid
                    columns={{ base: 1, md: 2 }}
                    gap="20px"
                    w="100%"
                    templateColumns={{ md: "1fr 1fr" }}
                  >
                    <CInput
                      label="Title"
                      type="text"
                      value={notificationDetail.title}
                      onChange={(value) => setNotificationDetail({ ...notificationDetail, title: value })}
                      required={true}
                      validated={validated}
                      placeholder="Enter Title"
                      w="100%"
                    />
                    <CInput
                      label="Description"
                      type="textarea"
                      value={notificationDetail.description}
                      onChange={(value) => setNotificationDetail({ ...notificationDetail, description: value })}
                      required={true}
                      validated={validated}
                      placeholder="Enter Description"
                      w="100%"
                      mb="8px !important"
                    />
                    <Checkbox
                      colorScheme={"orange"}
                      isChecked={showSchedule}
                      onChange={(e) => setShowSchedule(e.target.checked)}
                    >Set notification schedule</Checkbox>
                    {showSchedule && (
                      <DateTimePicker
                        label="Date and Time"
                        value={notificationDetail.scheduled_at}
                        onChange={(value) => setNotificationDetail({ ...notificationDetail, scheduled_at: value })}
                      />
                    )}
                  </SimpleGrid>
                </Stack>

                <Flex justify="flex-end" align="center" mt="44px" gap="3">
                  <Button
                    variant="darkBrand"
                    fontSize="sm"
                    borderRadius="10px"
                    w={{ base: '128px', md: '148px' }}
                    h="46px"
                    type="button"
                    bg="gray"
                    color="white"
                    _hover={{ bg: 'gray.600' }}
                    onClick={() => navigate('/admin/notifications')}
                  >
                    Cancel
                  </Button>
                  <CButton
                    w={{ base: '128px', md: '148px' }}
                    h="46px"
                    type="submit"
                  >
                    Submit
                  </CButton>
                </Flex>
              </Flex>
            </form>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Flex>
  );
}